// Multilingual SEO metadata for the homepage.
// Each entry provides a localized title, description and keyword list.
// Used by the root route to emit per-language <title>, <meta description>,
// hreflang alternates and og:locale alternates so the site can rank in
// search engines for native-language queries.

import type { Lang } from "@/lib/i18n";

// BCP-47 hreflang codes (Google-recognised) per app language.
export const HREFLANG: Record<Lang, string> = {
  en: "en",
  es: "es",
  fr: "fr",
  de: "de",
  it: "it",
  pt: "pt",
  zh: "zh",
  ja: "ja",
  th: "th",
  id: "id",
  fil: "fil",
  hi: "hi",
  tr: "tr",
  ar: "ar",
  ru: "ru",
  ko: "ko",
  pl: "pl",
  ro: "ro",
};

// Open Graph locale identifiers.
export const OG_LOCALE: Record<Lang, string> = {
  en: "en_US",
  es: "es_ES",
  fr: "fr_FR",
  de: "de_DE",
  it: "it_IT",
  pt: "pt_BR",
  zh: "zh_CN",
  ja: "ja_JP",
  th: "th_TH",
  id: "id_ID",
  fil: "fil_PH",
  hi: "hi_IN",
  tr: "tr_TR",
  ar: "ar_AR",
  ru: "ru_RU",
  ko: "ko_KR",
  pl: "pl_PL",
  ro: "ro_RO",
};

export type SeoStrings = { title: string; description: string; keywords: string };

export const SEO_I18N: Record<Lang, SeoStrings> = {
  en: {
    title: "Hantavirus International — Live Outbreak Map & Tracker",
    description:
      "Real-time global hantavirus outbreak tracker. Live case map, country reports, prevention guides and variant reference (ANDV, SNV, HTNV, SEOV, PUUV) sourced from WHO, CDC, UKHSA and NCID.",
    keywords:
      "hantavirus, hantavirus outbreak, hantavirus map, ANDV, Andes virus, Sin Nombre, HFRS, HPS, HCPS, MV Hondius, live outbreak tracker, WHO, CDC, public health",
  },
  es: {
    title: "Hantavirus Internacional — Mapa y Rastreador de Brotes en Vivo",
    description:
      "Rastreador mundial en tiempo real de brotes de hantavirus. Mapa de casos en vivo, informes por país, guías de prevención y referencia de variantes (ANDV, virus Sin Nombre) según OMS, CDC y ministerios.",
    keywords:
      "hantavirus, brote hantavirus, mapa hantavirus, virus Andes, ANDV, Sin Nombre, fiebre hemorrágica, síndrome pulmonar, OMS, CDC, salud pública",
  },
  fr: {
    title: "Hantavirus International — Carte et Suivi des Épidémies en Direct",
    description:
      "Suivi mondial en temps réel des épidémies d'hantavirus. Carte des cas en direct, rapports par pays, guides de prévention et référence des variants (ANDV, SNV, HTNV) selon l'OMS, le CDC et l'ECDC.",
    keywords:
      "hantavirus, épidémie hantavirus, carte hantavirus, virus Andes, ANDV, fièvre hémorragique, syndrome pulmonaire, OMS, CDC, ECDC, santé publique",
  },
  de: {
    title: "Hantavirus International — Live-Karte & Ausbruchstracker",
    description:
      "Globaler Echtzeit-Tracker für Hantavirus-Ausbrüche. Live-Fallkarte, Länderberichte, Präventionsleitfäden und Varianten-Referenz (ANDV, SNV, HTNV, PUUV) nach WHO, CDC, RKI und ECDC.",
    keywords:
      "Hantavirus, Hantavirus Ausbruch, Hantavirus Karte, Andes-Virus, ANDV, hämorrhagisches Fieber, Lungensyndrom, WHO, CDC, RKI, öffentliche Gesundheit",
  },
  it: {
    title: "Hantavirus International — Mappa e Tracker dei Focolai in Diretta",
    description:
      "Tracker globale in tempo reale dei focolai di hantavirus. Mappa dei casi in diretta, rapporti per paese, guide alla prevenzione e riferimento varianti (ANDV, SNV) secondo OMS, CDC e ECDC.",
    keywords:
      "hantavirus, focolaio hantavirus, mappa hantavirus, virus Andes, ANDV, febbre emorragica, sindrome polmonare, OMS, CDC, salute pubblica",
  },
  pt: {
    title: "Hantavírus Internacional — Mapa e Rastreador de Surtos ao Vivo",
    description:
      "Rastreador global em tempo real de surtos de hantavírus. Mapa de casos ao vivo, relatórios por país, guias de prevenção e referência de variantes (ANDV, Sin Nombre) segundo OMS, CDC e Ministério da Saúde.",
    keywords:
      "hantavírus, surto hantavírus, mapa hantavírus, vírus Andes, ANDV, febre hemorrágica, síndrome pulmonar, OMS, CDC, saúde pública",
  },
  zh: {
    title: "国际汉坦病毒 — 实时疫情地图与追踪",
    description:
      "实时全球汉坦病毒疫情追踪。实时病例地图、国家报告、预防指南以及变种参考（ANDV、SNV、HTNV、SEOV、PUUV），数据源自世卫组织、美国CDC、英国UKHSA和NCID。",
    keywords:
      "汉坦病毒, 汉坦病毒疫情, 汉坦病毒地图, 安第斯病毒, ANDV, 出血热, 肺综合征, 世卫组织, CDC, 公共卫生",
  },
  ja: {
    title: "ハンタウイルス・インターナショナル — ライブ流行マップ＆追跡",
    description:
      "世界のハンタウイルス流行をリアルタイムで追跡。ライブ症例マップ、国別レポート、予防ガイド、変異株リファレンス（ANDV、SNV、HTNV、PUUV）。WHO、CDC、UKHSA、NCID出典。",
    keywords:
      "ハンタウイルス, アウトブレイク, 流行マップ, アンデスウイルス, ANDV, 出血熱, 肺症候群, WHO, CDC, 公衆衛生",
  },
  th: {
    title: "ฮันตาไวรัสนานาชาติ — แผนที่และตัวติดตามการระบาดสด",
    description:
      "ติดตามการระบาดของฮันตาไวรัสทั่วโลกแบบเรียลไทม์ แผนที่ผู้ป่วยสด รายงานรายประเทศ คู่มือการป้องกัน และข้อมูลสายพันธุ์ (ANDV, SNV) จาก WHO, CDC, UKHSA และ NCID",
    keywords:
      "ฮันตาไวรัส, การระบาดฮันตาไวรัส, แผนที่ฮันตาไวรัส, ไวรัสแอนดีส, ANDV, ไข้เลือดออก, กลุ่มอาการปอด, WHO, CDC, สาธารณสุข",
  },
  id: {
    title: "Hantavirus Internasional — Peta & Pelacak Wabah Langsung",
    description:
      "Pelacak wabah hantavirus global secara real-time. Peta kasus langsung, laporan per negara, panduan pencegahan, dan referensi varian (ANDV, SNV) dari WHO, CDC, UKHSA, dan NCID.",
    keywords:
      "hantavirus, wabah hantavirus, peta hantavirus, virus Andes, ANDV, demam berdarah, sindrom paru, WHO, CDC, kesehatan masyarakat",
  },
  fil: {
    title: "Hantavirus International — Live na Mapa at Tracker ng Pagsiklab",
    description:
      "Real-time na pandaigdigang tracker ng pagsiklab ng hantavirus. Live na mapa ng kaso, ulat bawat bansa, gabay sa pag-iwas, at sanggunian ng variant (ANDV, SNV) mula sa WHO, CDC, UKHSA at NCID.",
    keywords:
      "hantavirus, pagsiklab ng hantavirus, mapa ng hantavirus, Andes virus, ANDV, hemorrhagic fever, pulmonary syndrome, WHO, CDC, kalusugang pampubliko",
  },
  hi: {
    title: "हंतावायरस इंटरनेशनल — लाइव प्रकोप मानचित्र और ट्रैकर",
    description:
      "हंतावायरस के वैश्विक प्रकोप का रीयल-टाइम ट्रैकर। लाइव मामले का मानचित्र, देश रिपोर्ट, रोकथाम गाइड और वेरिएंट संदर्भ (ANDV, SNV, HTNV)। WHO, CDC, UKHSA और NCID स्रोत।",
    keywords:
      "हंतावायरस, हंतावायरस प्रकोप, हंतावायरस मानचित्र, एंडीस वायरस, ANDV, रक्तस्रावी बुखार, फुफ्फुसीय सिंड्रोम, WHO, CDC, सार्वजनिक स्वास्थ्य",
  },
  tr: {
    title: "Hantavirüs International — Canlı Salgın Haritası ve Takipçisi",
    description:
      "Küresel hantavirüs salgınının gerçek zamanlı takibi. Canlı vaka haritası, ülke raporları, korunma rehberleri ve varyant referansı (ANDV, SNV, HTNV, PUUV). DSÖ, CDC, UKHSA ve NCID kaynaklı.",
    keywords:
      "hantavirüs, hantavirüs salgını, hantavirüs haritası, Andes virüsü, ANDV, kanamalı ateş, akciğer sendromu, DSÖ, CDC, halk sağlığı",
  },
  ar: {
    title: "هانتا فيروس الدولي — خريطة وتتبع التفشي المباشر",
    description:
      "متعقب عالمي لحظي لتفشي فيروس هانتا. خريطة حالات مباشرة، تقارير دول، أدلة وقاية ومرجع للمتغيرات (ANDV، SNV، HTNV) من منظمة الصحة العالمية وCDC وUKHSA وNCID.",
    keywords:
      "فيروس هانتا, تفشي هانتا, خريطة هانتا, فيروس الأنديز, ANDV, الحمى النزفية, متلازمة رئوية, منظمة الصحة العالمية, CDC, الصحة العامة",
  },
  ru: {
    title: "Хантавирус International — Карта и Трекер Вспышек в Реальном Времени",
    description:
      "Глобальный трекер вспышек хантавируса в реальном времени. Карта случаев, страновые отчёты, руководства по профилактике и справочник вариантов (ANDV, SNV, HTNV, PUUV). Источники: ВОЗ, CDC, UKHSA, NCID.",
    keywords:
      "хантавирус, вспышка хантавируса, карта хантавируса, вирус Анд, ANDV, геморрагическая лихорадка, лёгочный синдром, ВОЗ, CDC, общественное здоровье",
  },
  ko: {
    title: "한타바이러스 인터내셔널 — 실시간 발병 지도 및 추적기",
    description:
      "한타바이러스 발병에 대한 실시간 글로벌 추적기. 실시간 사례 지도, 국가별 보고서, 예방 가이드 및 변종 참고자료(ANDV, SNV, HTNV, PUUV). 출처: WHO, CDC, UKHSA, NCID.",
    keywords:
      "한타바이러스, 한타바이러스 발병, 한타바이러스 지도, 안데스 바이러스, ANDV, 출혈열, 폐 증후군, WHO, CDC, 공중보건",
  },
  pl: {
    title: "Hantawirus International — Mapa i śledzenie ognisk na żywo",
    description:
      "Globalny tracker ognisk hantawirusa w czasie rzeczywistym. Mapa przypadków na żywo, raporty krajowe, przewodniki profilaktyki i odniesienia do wariantów (ANDV, SNV, HTNV, PUUV) na podstawie WHO, CDC, UKHSA i NCID.",
    keywords:
      "hantawirus, ognisko hantawirusa, mapa hantawirusa, wirus Andes, ANDV, gorączka krwotoczna, zespół płucny, WHO, CDC, zdrowie publiczne",
  },
  ro: {
    title: "Hantavirus International — Hartă și monitorizare focare în direct",
    description:
      "Monitor global în timp real al focarelor de hantavirus. Hartă live a cazurilor, rapoarte pe țări, ghiduri de prevenire și referință variante (ANDV, SNV, HTNV, PUUV) din surse OMS, CDC, UKHSA și NCID.",
    keywords:
      "hantavirus, focar hantavirus, hartă hantavirus, virusul Andes, ANDV, febră hemoragică, sindrom pulmonar, OMS, CDC, sănătate publică",
  },
};

export const ALL_LANGS: Lang[] = [
  "en", "es", "fr", "de", "it", "pt", "zh", "ja", "th", "id", "fil", "hi", "tr", "ar", "ru", "ko", "pl", "ro",
];
